import type { TripState } from "./types";

const STORAGE_KEY = "tripPlanner.library.v1";

type Library = { trips: TripState[] };

/**
 * The durable archive of every trip. `tripPlanner.v1` (see `stops.ts`) is only the working
 * copy of the *active* trip; this store keeps a full snapshot of each trip keyed by `id`, and
 * the active trip is mirrored in here (via `upsert`) on every change from `main.ts`.
 *
 * Snapshots include `share` because the library is local-only and never exported.
 */
class SavedTripsStore extends EventTarget {
  private trips: TripState[] = [];

  load(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Library;
        if (Array.isArray(parsed?.trips)) {
          this.trips = parsed.trips.filter(
            (t) => t && typeof t.id === "string" && Array.isArray(t.stops),
          );
        }
      }
    } catch {
      this.trips = [];
    }
    this.emit();
  }

  list(): readonly TripState[] {
    return this.trips;
  }

  get(id: string): TripState | undefined {
    const found = this.trips.find((t) => t.id === id);
    return found ? structuredClone(found) : undefined;
  }

  upsert(state: TripState): void {
    if (!state.id) return;
    const idx = this.trips.findIndex((t) => t.id === state.id);
    const snapshot = structuredClone(state);
    if (idx === -1) this.trips.push(snapshot);
    else this.trips[idx] = snapshot;
    this.persist();
    this.emit();
  }

  remove(id: string): void {
    const before = this.trips.length;
    this.trips = this.trips.filter((t) => t.id !== id);
    if (this.trips.length === before) return;
    this.persist();
    this.emit();
  }

  private persist(): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ trips: this.trips } satisfies Library));
  }

  private emit(): void {
    this.dispatchEvent(new CustomEvent("change"));
  }
}

export const savedTrips = new SavedTripsStore();
